import {
  ADMIN,
  APPROVED,
  AWAITING_DELIVERY,
  DRIVER,
  MEMBERSHIP,
  STAFF,
  SUPER_ADMIN,
  TUTOR,
} from "../config/constants";
import { camelCase2Words, properlizeWords } from "../helpers";
import { IAuthLevelUser, IUser } from "./reducers/authReducer";
import { ICustomer } from "./reducers/customersReducer";
import { IOrder } from "./reducers/ordersReducer";
import { IRentalItem } from "./reducers/rentalItemsReducer";
import { IRentalPackage } from "./reducers/rentalPackagesReducer";
import { ISite } from "./reducers/sitesReducer";
import { IStaff } from "./reducers/staffsReducer";

export interface IAPIFieldError {
  location: string;
  msg: string;
  path: string;
  type: string;
  value: string;
}

export interface IArrItemErr {
  index: number;
  msg: string;
}

export interface IFieldError {
  field: string;
  msg: string;
}

export const getFieldErrors = (errors: IAPIFieldError[]) => {
  let fieldErrors: IFieldError[] = [];
  let n = 0;

  for (const error of errors) {
    fieldErrors[n++] = { field: error.path, msg: error.msg };
  }

  return fieldErrors;
};

export class QueryStringBuilder {
  private inputs: { field: string; value: string }[] = [];

  addInput(field: string, value: string) {
    this.inputs[this.inputs.length] = { field, value };
  }

  getString() {
    let itr = 0;
    let str = "";

    for (const input of this.inputs) {
      str += itr++ === 0 ? "" : "&";
      str += `${input.field}=${input.value}`;
    }

    this.inputs = [];

    return str !== "" ? `?${str}` : "";
  }
}

export function userIsAuthenticated(user?: IUser) {
  if (!user) {
    return false;
  }
  if (user.usesAuthenticator2FA) {
    if (!user.authenticator2FADone) {
      return false;
    }
  }

  if (user.usesEmail2FA) {
    if (!user.email2FADone) {
      return false;
    }
  }

  return true;
}

export const interpretDataIntoXlsxFormat = (data: any) => {
  let columns: any[] = [];
  let content: any[] = [];

  let newData: any[] = [];

  for (const key in data[0]) {
    columns = [
      ...columns,
      {
        label: properlizeWords(camelCase2Words(key)),
        value: key,
      },
    ];
  }

  for (const order of data) {
    content = [...content, order];
  }

  newData = [
    {
      sheet: "RentalOrders",
      columns,
      content,
    },
  ];

  return newData;
};

export const getUnitsNames = (units: any[]) => {
  let newUnits: string[] = [];

  for (const unit of units) {
    newUnits = [...newUnits, unit.name.toLowerCase()];
  }

  return newUnits;
};

export const rentalItemExcelData2DBData = (data: any) => {
  let dbData = {};

  const requiredProps = {
    "Rental Item Name": "name",
    "Rental Item Total Count": "count",
    "Rental Item Price Per Day": "pricePerDay",
  };

  const optionalProps = {};

  for (const requiredProp in requiredProps) {
    let propIsPresent = false;

    for (const presentProp in data) {
      if (presentProp === requiredProp) {
        propIsPresent = true;
        break;
      }
    }

    if (!propIsPresent) {
      throw new Error(`column "${requiredProp}" is missing.`);
    }

    if (
      data[requiredProp as keyof typeof data] &&
      data[requiredProp as keyof typeof data] !== ""
    ) {
      dbData = {
        ...dbData,
        [requiredProps[requiredProp as keyof typeof requiredProps]]:
          data[requiredProp as keyof typeof data] ?? undefined,
      };
    }
  }

  for (const optionalProp in optionalProps) {
    let propIsPresent = false;

    if (
      data[optionalProp as keyof typeof data] &&
      data[optionalProp as keyof typeof data] !== ""
    ) {
      dbData = {
        ...dbData,
        [optionalProps[optionalProp as keyof typeof optionalProps]]:
          data[optionalProp as keyof typeof data] ?? undefined,
      };
    }
  }

  return dbData;
};

export const countRentalTypes = (rentalItems: IRentalItem[], type: string) => {
  let count = 0;

  for (const item of rentalItems) {
    if (item?.type === type) {
      ++count;
    }
  }

  return count;
};

export const rentalItemLiked = (
  rentalItem: IRentalItem,
  favouriteRentalItems: IRentalItem[]
) => {
  for (const favItem of favouriteRentalItems) {
    if (favItem._id === rentalItem._id) {
      return true;
    }
  }

  return false;
};

export const membershipMatchType = (
  membership: IOrder | null,
  type: string
) => {
  if (membership && (membership.rentalPackage?.rentalItems)[0].name === type) {
    return true;
  }
  return false;
};

const createPseudoRentalItem = (name: string, type: string) => {
  return {
    _id: "dummy",
    name: name,
    type: type,
    imgSrc: "dummy.png",
    pricePerDay: 0,
    count: 0,
    rating: 0,
    availableCount: 0,
    createdAt: new Date().toDateString(),
  } as IRentalItem;
};

const createPseudoRentalPackage = (
  rentalItemName: string,
  rentalItemType: string
) => {
  return {
    _id: "dummy",
    rentalItems: [createPseudoRentalItem(rentalItemName, rentalItemType)],
    rentalDaysCount: 0,
    timeFrameExtensions: [],
    transportationFee: 0,
    totalCost: 0,
    status: AWAITING_DELIVERY,
    deliveryDriver: null,
    deliveryTimeStamp: null,
    retrievalDriver: null,
    retrievalTimeStamp: null,
    createAt: new Date().toDateString(),
    updatedAt: new Date().toDateString(),
  } as IRentalPackage;
};

const createPseudoSite = () => {
  return {
    _id: "dummy",
    owner: {
      type: "dummy",
      id: "dummy",
    },
    name: "dummy",
    address: "dummy",
  } as ISite;
};

const createPseudoCustomer = (user: IUser) => {
  return {
    ...user,
    _id: "dummy",
    userId: user._id,
    deliveryAddress: "dummy",
    ordersCount: 0,
    orderRequestsCount: 0,
    creditBalance: 0,
  } as ICustomer;
};

const createPseudoStaff = (user: IUser) => {
  return {
    ...user,
  } as IStaff;
};

export const createPseudoActiveMembership = (type: string, user: IUser) => {
  const rentalPackage = createPseudoRentalPackage(type, MEMBERSHIP);

  return {
    _id: "dummy",
    ref: "dummy",
    creator: createPseudoCustomer(user),
    customer: createPseudoCustomer(user),
    staff: createPseudoStaff(user),
    rentalPackage,
    site: createPseudoSite(),
    paymentTypes: [],
    payments: [],
    status: APPROVED,
    approvalTimeStamp: new Date().getTime(),
    departureTimeStamp: new Date().getTime(),
    updates: [],
    createAt: new Date().toDateString(),
    updateAt: new Date().toDateString(),
    deliveryDueDate: new Date().toDateString(),
  } as IOrder;
};

export const userIsAWorker = (user: IUser | IAuthLevelUser) => {
  const workers = [SUPER_ADMIN, ADMIN, STAFF, TUTOR, DRIVER];

  for (const worker of workers) {
    if (user?.role?.name === worker) {
      return true;
    }
  }

  return false;
};

export const customerExcelData2DBData = (data: any) => {
  let dbData = {};

  const requiredProps = {
    "Customer First Name": "firstName",
    "Customer Last Name": "lastName",
    "Customer Email": "email",
    "Customer Phone Number": "phoneNumber",
    "Customer Delivery Address": "deliveryAddress",

    "Customer Company Name": "compName",
    "Customer Company Country": "compCountry",
    "Customer Company State": "compState",
    "Customer Company Phone Number": "compPhoneNumber",
    "Customer Company Email": "compEmail",
  };

  const optionalProps = {
    "Customer Credit Balance": "creditBalance",

    "Customer Company Main Office Address": "compMainOfficeAddress",
    "Customer Company Fax Number": "compFaxNumber",
    "Customer Company Website": "compWebsite",
  };

  for (const requiredProp in requiredProps) {
    let propIsPresent = false;

    for (const presentProp in data) {
      if (presentProp === requiredProp) {
        propIsPresent = true;
        break;
      }
    }

    if (!propIsPresent) {
      throw new Error(`column "${requiredProp}" is missing.`);
    }

    if (
      data[requiredProp as keyof typeof data] &&
      data[requiredProp as keyof typeof data] !== ""
    ) {
      dbData = {
        ...dbData,
        [requiredProps[requiredProp as keyof typeof requiredProps]]:
          data[requiredProp as keyof typeof data] ?? undefined,
      };
    }
  }

  for (const optionalProp in optionalProps) {
    let propIsPresent = false;

    if (
      data[optionalProp as keyof typeof data] &&
      data[optionalProp as keyof typeof data] !== ""
    ) {
      dbData = {
        ...dbData,
        [optionalProps[optionalProp as keyof typeof optionalProps]]:
          data[optionalProp as keyof typeof data] ?? undefined,
      };
    }
  }

  return dbData;
};
